import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Text,
} from "@/components/ui";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import PurifiedHtml from "@/components/ui/PurifiedHtml/PurifiedHtml";
import { SelectDestinationFolderSlideover } from "@/components/features/matter/file-repo/signature-requests/SelectDestinationFolderSlideover";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  matterEmailPreviewQueryOptions,
  useDeleteMatterEmail,
} from "@/lib/data/react-query/matters/matter-email";
import { useCreateMatterFile } from "@/lib/data/react-query/matters/matter-file";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  Matter,
  MatterEmailWithAttachments,
  MatterFile,
} from "@/lib/types";
import type { APIResponse } from "@/lib/types/APIResponse";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import {
  convertPlainTextEmailToHtml,
  downloadMatterEmailAsset,
  getMatterEmailAttachmentName,
} from "@/lib/utils/matterEmail";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileCheck, FilePlus, FileText, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

function senderInitials(fromLine: string): string {
  const trimmed = fromLine.trim();
  if (!trimmed) return "?";
  const displayMatch = trimmed.match(/^(.+?)\s*<[^>]+>\s*$/);
  const beforeBracket = (displayMatch?.[1] ?? trimmed.split("<")[0]).trim();
  const namePart = beforeBracket.includes("@")
    ? (beforeBracket.split("@")[0] ?? beforeBracket)
    : beforeBracket;
  const parts = namePart.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    const pair = (a + b).toUpperCase();
    return pair || "?";
  }
  if (namePart.length >= 2) return namePart.slice(0, 2).toUpperCase();
  const first = namePart[0];
  return first ? first.toUpperCase() : "?";
}

export interface MatterEmailDetailContentProps {
  matterId: string;
  matterEmailId: string;
  matter?: Matter;
  onDeleteSuccess?: () => void;
  className?: string;
}

function MatterEmailDetailSkeleton({
  showHeader = true,
}: {
  showHeader?: boolean;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {showHeader ? (
        <div className="bg-card z-20 border-b">
          <div className="space-y-3 px-4 pt-4 sm:px-5 sm:pt-5">
            <Skeleton className="h-6 w-2/3 rounded-md" />
          </div>
          <div className="mt-3 flex items-start gap-3 px-4 pb-4 sm:px-5 sm:pb-5">
            <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-40 rounded-md" />
              <Skeleton className="h-3 w-56 rounded-md" />
              <Skeleton className="h-3 w-48 rounded-md" />
            </div>
          </div>
        </div>
      ) : null}
      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
        <div className="flex min-w-0 flex-col gap-6 py-4 sm:py-5">
          <div className="border-border mx-5 space-y-3 border-b px-1 pb-6 sm:mx-6 sm:pb-8">
            <Skeleton className="h-4 w-full rounded-md" />
            <Skeleton className="h-4 w-[92%] rounded-md" />
            <Skeleton className="h-4 w-[78%] rounded-md" />
            <Skeleton className="h-4 w-[88%] rounded-md" />
            <Skeleton className="h-4 w-[64%] rounded-md" />
          </div>
          <div className="space-y-3 px-5 pb-5 sm:px-6 sm:pb-6">
            <Skeleton className="h-5 w-32 rounded-md" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function MatterEmailDetailContent({
  matterId,
  matterEmailId,
  matter: matterProp,
  onDeleteSuccess,
  className,
}: MatterEmailDetailContentProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [attachmentImportState, setAttachmentImportState] = useState<
    Record<string, "idle" | "loading" | "added">
  >({});
  const [folderSelectOpen, setFolderSelectOpen] = useState(false);
  const [pendingAttachmentIds, setPendingAttachmentIds] = useState<string[]>(
    [],
  );
  const { hasActionPermission } = useUser();
  const queryClient = useQueryClient();
  const createMatterFile = useCreateMatterFile();
  const cachedMatter = queryClient.getQueryData<APIResponse<Matter>>(
    matterDetailQueryKey(matterId),
  );
  const { data: fetchedMatter, isLoading: isMatterLoading } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: Boolean(matterId) && !matterProp && !cachedMatter,
  });
  const matterData = matterProp
    ? ({ data: matterProp } as APIResponse<Matter>)
    : (cachedMatter ?? fetchedMatter);
  const matter = matterData?.data;
  const matterFiles = matter?.files;
  const matterFilePresignedKeys = useMemo(() => {
    const keys = new Set<string>();
    matterFiles?.forEach((file) => {
      if (file.presignedUrlKey) {
        keys.add(file.presignedUrlKey);
      }
    });
    return keys;
  }, [matterFiles]);
  const email = useMemo<MatterEmailWithAttachments | undefined>(() => {
    if (!matter) return undefined;
    const foundEmail = matter.emails.find(
      (entry) => entry.id === matterEmailId,
    );
    if (!foundEmail) return undefined;
    const attachments = matter.emailAttachments.filter(
      (attachment) => attachment.emailId === matterEmailId,
    );
    return { ...foundEmail, attachments };
  }, [matter, matterEmailId]);

  useEffect(() => {
    setDeleteModalOpen(false);
    setAttachmentImportState({});
    setFolderSelectOpen(false);
    setPendingAttachmentIds([]);
  }, [matterEmailId]);

  const attachments = useMemo(() => email?.attachments ?? [], [email]);

  const getAttachmentName = useCallback(
    (attachment: MatterEmailWithAttachments["attachments"][number]) =>
      getMatterEmailAttachmentName(attachment),
    [],
  );
  const isAttachmentInRepo = useCallback(
    (attachment: MatterEmailWithAttachments["attachments"][number]) => {
      const key = attachment.presignedUrlKey;
      if (!key) return false;
      return matterFilePresignedKeys.has(key);
    },
    [matterFilePresignedKeys],
  );

  const canDeleteEmail = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterEmail,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canAddAttachmentsToRepo = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.create,
  );

  const deleteEmailMutation = useDeleteMatterEmail();

  const emailTimestamp = email
    ? email.dateCreated || email.dateModified || ""
    : "";
  const primaryFromLine = email?.fromEmails?.[0]?.trim() ?? "";
  const fromInitials = useMemo(
    () => senderInitials(primaryFromLine),
    [primaryFromLine],
  );
  const longTimestamp = useMemo(() => {
    if (!emailTimestamp) return "";
    const local = convertToLocalFromDatabaseDateTime(emailTimestamp);
    if (!local) return "";
    const d = dayjs(local);
    return d.isValid() ? d.format("dddd, MMMM D, YYYY [at] h:mm A") : "";
  }, [emailTimestamp]);

  const nonEmailAttachments = useMemo(() => {
    return attachments.filter((attachment) => {
      const attachmentName = attachment.name?.toLowerCase() ?? "";
      if (!attachmentName) return false;
      return !attachmentName.endsWith(".eml");
    });
  }, [attachments]);

  const emlAttachment = useMemo(() => {
    return attachments.find((attachment) => {
      const attachmentName = attachment.name?.toLowerCase() ?? "";
      const isEml = !attachmentName || attachmentName.endsWith(".eml");
      return isEml && attachment.presignedUrlKey;
    });
  }, [attachments]);

  const emailDownloadKey =
    email?.presignedUrlKey || emlAttachment?.presignedUrlKey || "";
  const emailAttachmentId = email?.presignedUrlKey
    ? undefined
    : emlAttachment?.id;

  const {
    data: emailPreview,
    isLoading: isEmailPreviewLoading,
    isError: isEmailPreviewError,
  } = useQuery(
    matterEmailPreviewQueryOptions({
      emailId: matterEmailId,
      attachmentId: emailAttachmentId,
      key: emailDownloadKey,
    }),
  );
  const emailHtmlContent = useMemo(() => {
    if (emailPreview?.html) return emailPreview.html;
    if (emailPreview?.text) {
      return convertPlainTextEmailToHtml(emailPreview.text);
    }
    return undefined;
  }, [emailPreview]);

  const handleAttachmentAddToRepo = async (
    attachment: MatterEmailWithAttachments["attachments"][number],
    folderId?: string,
  ) => {
    const attachmentPresignedKey = attachment.presignedUrlKey;
    if (!attachmentPresignedKey || !canAddAttachmentsToRepo) {
      return;
    }
    const attachmentId = attachment.id;
    setAttachmentImportState((prev) => ({
      ...prev,
      [attachmentId]: "loading",
    }));
    try {
      const fileName = getAttachmentName(attachment);
      await createMatterFile.mutateAsync({
        matterId,
        isFolder: false,
        idLocation: folderId ?? "",
        name: fileName,
        presignedUrlKey: attachmentPresignedKey,
      });
      setAttachmentImportState((prev) => ({
        ...prev,
        [attachmentId]: "added",
      }));
    } catch (error) {
      console.error("Error adding attachment to file repo", error);
      toast.error("Unable to add attachment to file repo");
      setAttachmentImportState((prev) => ({
        ...prev,
        [attachmentId]: "idle",
      }));
    }
  };

  useEffect(() => {
    if (!folderSelectOpen) {
      setPendingAttachmentIds([]);
    }
  }, [folderSelectOpen]);

  const attachmentById = useMemo(() => {
    return new Map(
      nonEmailAttachments.map((attachment) => [attachment.id, attachment]),
    );
  }, [nonEmailAttachments]);

  const pendingAttachments = useMemo(() => {
    return pendingAttachmentIds
      .map((id) => attachmentById.get(id))
      .filter(
        (
          attachment,
        ): attachment is MatterEmailWithAttachments["attachments"][number] =>
          Boolean(attachment),
      );
  }, [attachmentById, pendingAttachmentIds]);

  const handleOpenFolderSelect = (
    attachmentIds: string[],
    fallbackLabel?: string,
  ) => {
    if (!canAddAttachmentsToRepo || attachmentIds.length === 0) {
      if (fallbackLabel) {
        toast.error(fallbackLabel);
      }
      return;
    }
    setPendingAttachmentIds(attachmentIds);
    setFolderSelectOpen(true);
  };

  const handleSaveAttachmentsToFolder = async (folder: MatterFile | null) => {
    if (pendingAttachments.length === 0) {
      setFolderSelectOpen(false);
      return;
    }
    setFolderSelectOpen(false);
    await Promise.all(
      pendingAttachments.map((attachment) =>
        handleAttachmentAddToRepo(attachment, folder?.id),
      ),
    );
    setPendingAttachmentIds([]);
  };

  const handleDownloadEmail = () => {
    if (!emailDownloadKey) return;
    const fileName = `${
      email?.subject?.trim() || "matter-email"
    }-${email?.id}.eml`;
    void downloadMatterEmailAsset({
      emailId: matterEmailId,
      attachmentId: emailAttachmentId,
      fileName,
    });
  };

  const attachmentCount = nonEmailAttachments.length;

  if (!email) {
    return (
      <div className={`flex flex-1 ${className ?? ""}`}>
        {isMatterLoading && !matterProp ? (
          <MatterEmailDetailSkeleton showHeader={false} />
        ) : (
          <Text variant="subHeading" className="m-auto p-6">
            Unable to load email details.
          </Text>
        )}
      </div>
    );
  }

  const emailBodyProseClassName =
    "prose prose-sm max-w-none min-w-0 text-sm break-words text-foreground [&_a]:break-words [&_img]:h-auto [&_img]:max-w-full [&_picture]:max-w-full [&_svg]:max-w-full [&_video]:max-w-full [&_iframe]:max-w-full [&_table]:max-w-full [&_td]:break-words [&_th]:break-words [&_pre]:max-w-full [&_pre]:overflow-x-hidden [&_pre]:whitespace-pre-wrap [&_pre]:break-words";

  return (
    <TooltipProvider delayDuration={120}>
      <div
        className={`flex h-full min-h-0 min-w-0 flex-col ${className ?? ""}`}
      >
        <div className="bg-card z-20 border-b">
          <div className="min-w-0 space-y-1 px-4 pt-4 sm:px-5 sm:pt-5">
            <Text
              variant="cardTitle"
              className="leading-snug break-words sm:text-lg"
            >
              {email.subject || "No subject"}
            </Text>
          </div>

          <div className="mt-3 flex min-w-0 flex-wrap items-start gap-3 px-4 pb-4 sm:px-5 sm:pb-5">
            <div
              className="bg-muted text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              aria-hidden
            >
              {fromInitials}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <Text
                variant="body"
                className="leading-tight font-medium break-words"
              >
                {primaryFromLine || "Unknown sender"}
              </Text>
              {longTimestamp ? (
                <Text variant="caption" color="muted">
                  {longTimestamp}
                </Text>
              ) : (
                <Text variant="caption" color="muted">
                  Timestamp unavailable
                </Text>
              )}
              {(email.toEmails?.length ?? 0) > 0 ||
              (email.ccEmails?.length ?? 0) > 0 ? (
                <div className="space-y-0.5 pt-1">
                  {email.toEmails?.length ? (
                    <Text
                      variant="caption"
                      color="muted"
                      className="break-words"
                    >
                      <Text as="span" variant="caption" className="font-medium">
                        To:
                      </Text>{" "}
                      {email.toEmails.join(", ")}
                    </Text>
                  ) : null}
                  {email.ccEmails?.length ? (
                    <Text
                      variant="caption"
                      color="muted"
                      className="break-words"
                    >
                      <Text as="span" variant="caption" className="font-medium">
                        CC:
                      </Text>{" "}
                      {email.ccEmails.join(", ")}
                    </Text>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      disabled={!emailDownloadKey}
                      onClick={handleDownloadEmail}
                      className="h-8 w-8"
                      aria-label="Download email"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {emailDownloadKey ? "Download email" : "Download unavailable"}
                </TooltipContent>
              </Tooltip>
              {canDeleteEmail ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      className="text-destructive hover:text-destructive h-8 w-8 border-red-200 hover:bg-red-50"
                      onClick={() => {
                        setDeleteModalOpen(true);
                      }}
                      aria-label="Delete email"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete email</TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div className="flex min-w-0 flex-col gap-6 py-4 sm:py-5">
            <div className="border-border mx-5 border-b px-1 pb-6 sm:mx-6 sm:pb-8">
              {isEmailPreviewLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full rounded-md" />
                  <Skeleton className="h-4 w-[94%] rounded-md" />
                  <Skeleton className="h-4 w-[82%] rounded-md" />
                  <Skeleton className="h-4 w-[88%] rounded-md" />
                  <Skeleton className="h-4 w-[61%] rounded-md" />
                </div>
              ) : isEmailPreviewError ? (
                <Text variant="subHeading">
                  Unable to preview this email. Download the .eml file instead.
                </Text>
              ) : emailHtmlContent ? (
                <PurifiedHtml
                  html={emailHtmlContent}
                  fallback={
                    emailPreview?.text ? (
                      <pre className="text-foreground/90 max-w-full overflow-x-hidden font-mono text-xs break-words whitespace-pre-wrap">
                        {emailPreview.text}
                      </pre>
                    ) : undefined
                  }
                  className={emailBodyProseClassName}
                />
              ) : emailPreview?.text ? (
                <pre className="text-foreground/90 max-w-full overflow-x-hidden font-mono text-sm break-words whitespace-pre-wrap">
                  {emailPreview.text}
                </pre>
              ) : (
                <Text variant="subHeading">
                  No message preview. Download the email to view it.
                </Text>
              )}
            </div>

            <div className="min-w-0 space-y-3 px-5 pb-5 sm:px-6 sm:pb-6">
              <Text variant="body" className="text-lg font-semibold">
                Attachments ({attachmentCount})
              </Text>
              {nonEmailAttachments.length === 0 ? (
                <Card className="shadow-none">
                  <CardContent className="p-6 text-center">
                    <Text variant="subHeading">
                      This email has no file attachments.
                    </Text>
                  </CardContent>
                </Card>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {nonEmailAttachments.map((attachment) => {
                    const attachmentState =
                      attachmentImportState[attachment.id] ?? "idle";
                    const alreadyInRepo = isAttachmentInRepo(attachment);
                    const isLoading =
                      attachmentState === "loading" && !alreadyInRepo;
                    const isAdded =
                      alreadyInRepo || attachmentState === "added";
                    const canDownloadAttachment = Boolean(
                      attachment.presignedUrlKey,
                    );
                    const canSaveAttachment =
                      canAddAttachmentsToRepo &&
                      canDownloadAttachment &&
                      !isLoading &&
                      !isAdded;

                    return (
                      <div
                        key={attachment.id}
                        className="w-full max-w-sm min-w-0"
                      >
                        <div className="group hover:bg-muted/50 bg-muted/30 flex w-full min-w-0 items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors">
                          <div className="bg-muted text-muted-foreground flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border">
                            <FileText className="h-5 w-5" />
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="min-w-0 flex-1">
                                <Text
                                  variant="body"
                                  className="line-clamp-1 font-medium"
                                >
                                  {getAttachmentName(attachment)}
                                </Text>
                                <Text variant="caption" color="muted">
                                  {isAdded
                                    ? "Saved to file repo"
                                    : canDownloadAttachment
                                      ? "Attachment"
                                      : "Download unavailable"}
                                </Text>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-sm text-xs"
                            >
                              {getAttachmentName(attachment)}
                            </TooltipContent>
                          </Tooltip>
                          <div className="flex items-center gap-1 opacity-100 transition-opacity sm:pointer-events-none sm:opacity-0 sm:group-focus-within:pointer-events-auto sm:group-focus-within:opacity-100 sm:group-hover:pointer-events-auto sm:group-hover:opacity-100">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    type="button"
                                    disabled={!canDownloadAttachment}
                                    onClick={() => {
                                      if (!attachment.presignedUrlKey) {
                                        return;
                                      }
                                      void downloadMatterEmailAsset({
                                        emailId: matterEmailId,
                                        attachmentId: attachment.id,
                                        fileName: getAttachmentName(attachment),
                                      });
                                    }}
                                    className="h-7 w-7"
                                    aria-label={`Download ${getAttachmentName(attachment)}`}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {canDownloadAttachment
                                  ? "Download attachment"
                                  : "Download unavailable"}
                              </TooltipContent>
                            </Tooltip>
                            {canAddAttachmentsToRepo ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      type="button"
                                      disabled={!canSaveAttachment}
                                      onClick={() => {
                                        handleOpenFolderSelect(
                                          [attachment.id],
                                          "This attachment cannot be saved.",
                                        );
                                      }}
                                      className="h-7 w-7"
                                      aria-label={
                                        isAdded
                                          ? `${getAttachmentName(attachment)} added to File Repo`
                                          : `Add ${getAttachmentName(attachment)} to File Repo`
                                      }
                                    >
                                      {isLoading ? (
                                        <LoadingSpinner className="h-3.5 w-3.5" />
                                      ) : isAdded ? (
                                        <FileCheck className="h-3.5 w-3.5" />
                                      ) : (
                                        <FilePlus className="h-3.5 w-3.5" />
                                      )}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {isLoading
                                    ? "Saving to file repo"
                                    : isAdded
                                      ? "Added to file repo"
                                      : canSaveAttachment
                                        ? "Add to file repo"
                                        : "Cannot add to file repo"}
                                </TooltipContent>
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        {matter && (
          <SelectDestinationFolderSlideover
            open={folderSelectOpen}
            setOpen={setFolderSelectOpen}
            matter={matter}
            onFolderSelected={handleSaveAttachmentsToFolder}
            title="Select folder for attachments"
            description="Choose where these email attachments should be saved."
          />
        )}
        {canDeleteEmail && (
          <ActionConfirmationModal
            open={deleteModalOpen}
            setOpen={setDeleteModalOpen}
            title="Delete this email?"
            description="This will remove the email and all attachments."
            buttonText={
              deleteEmailMutation.isPending ? "Deleting..." : "Delete"
            }
            afterActionFn={async () => {
              if (deleteEmailMutation.isPending) return;
              setDeleteModalOpen(false);
              await deleteEmailMutation.mutateAsync({
                matterId,
                emailId: matterEmailId,
              });
              onDeleteSuccess?.();
            }}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
